import { Router, useLocation, useCurrentMatches, useSearchParams } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import { Suspense, ErrorBoundary, createEffect, Show } from "solid-js";
import { MetaProvider, Link, Meta } from "@solidjs/meta";
import "./App.css";
import "./index.css";

import { HreflangLinks } from "@/components/hreflang-links";
import Footer from "@/components/footer";
import { clientOnly } from "@solidjs/start";
import { isLowEndPC } from "@/utils/deviceCheck";
import { reportEvent, registerCrashHandlers, initVitals } from "@/utils/telemetry";

registerCrashHandlers();
initVitals();

const Toaster = clientOnly(async () => {
  const { Toaster } = await import("@/components/ui/sonner");
  return { default: Toaster };
});

function Analytics() {
  const location = useLocation();

  createEffect(() => {
    const path = location.pathname + location.search;
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
      });
    }
  });

  return null;
}

const Navigator = clientOnly(() => import("@/components/navigator"));

function RouteAwareNavigator() {
  const matches = useCurrentMatches();
  const [sp] = useSearchParams();
  const flat = () => matches().some((m) => m.route?.info?.flat);
  return <Show when={sp.view !== "full"}><Navigator flat={flat()} /></Show>;
}
function SiteFooter() {
  const location = useLocation();
  const [sp] = useSearchParams();
  const hidden = () => sp.view === "full" || ["/auth", "/welcome"].some((p) => location.pathname.startsWith(p));
  return <Show when={!hidden()}><Footer /></Show>;
}

export default function App() {
  createEffect(() => {
    if (isLowEndPC()) {
      document.documentElement.classList.add("low-end-pc");
    }
  });

  return (
    <Router
      root={(props) => (
        <MetaProvider>
          <Link rel="icon" type="image/x-icon" href="/favicon.ico" />
          <Link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
          <Link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
          <Link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
          <Link rel="preload" as="font" type="font/woff2" href="/fonts/hanken-grotesk-normal-latin.woff2" crossorigin="anonymous" />
          <Link rel="stylesheet" href="/fonts/fonts.css" />
          <Meta name="theme-color" content="#000000" />
          <Analytics />
          <HreflangLinks />
          <ErrorBoundary fallback={(err) => { reportEvent("render", String(err?.message ?? err)); console.error("Navigator crashed:", err); return null; }}>
            <Suspense>
              <RouteAwareNavigator />
            </Suspense>
          </ErrorBoundary>
            <ErrorBoundary fallback={(err, reset) => {
              reportEvent("render", String(err?.message ?? err));
              console.error(err);
              return (
                <div class="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6 text-center">
                  <p class="text-foreground font-medium">Something went wrong.</p>
                  <p class="text-muted-foreground text-sm font-mono">{String(err?.message ?? err)}</p>
                  <pre class="text-muted-foreground text-xs font-mono text-left max-w-full max-h-60 overflow-auto whitespace-pre-wrap">{err?.stack}</pre>
                  <button onClick={reset} class="px-4 py-2 text-sm font-medium bg-foreground text-background hover:opacity-90 transition-opacity">Try again</button>
                </div>
              );
            }}>
            <Suspense fallback={<div class="min-h-screen bg-background" />}>
              {props.children}
            </Suspense>
            </ErrorBoundary>
            <SiteFooter />
            <Toaster />
        </MetaProvider>
      )}
    >
      <FileRoutes />
    </Router>
  );
}
